import { useEffect, useMemo, useState } from 'react';
import { Camera, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/shared/PageHeader';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Avatar from '../components/ui/Avatar';
import Modal from '../components/ui/Modal';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { maskPhone } from '../utils/masks';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Perfil() {
  const { user, perfil } = useAuth();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    workshopName: '',
    avatar: '',
  });
  const [errors, setErrors] = useState({});
  const [passwordModal, setPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    setForm({
      name: perfil?.nome || user?.user_metadata?.name || '',
      email: user?.email || '',
      phone: perfil?.telefone || '',
      role: perfil?.perfil || '',
      workshopName: perfil?.oficina_nome || perfil?.workshopName || '',
      avatar: perfil?.avatar_url || user?.user_metadata?.avatar_url || '',
    });
  }, [perfil, user]);

  const initials = useMemo(() => (form.name || form.email || '?').split(' ').map((p) => p[0]).slice(0, 2).join(''), [form.name, form.email]);

  const onUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((prev) => ({ ...prev, avatar: String(reader.result) }));
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Nome é obrigatório';
    if (!form.email.trim()) next.email = 'Email é obrigatório';
    else if (!emailRegex.test(form.email)) next.email = 'Informe um email válido';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSave = async () => {
    if (!validate()) return;

    const { error: authError } = await supabase.auth.updateUser({
      email: form.email,
      data: {
        name: form.name,
        avatar_url: form.avatar,
      },
    });

    if (authError) {
      setErrors((prev) => ({ ...prev, email: authError.message }));
      return;
    }

    if (perfil?.id) {
      const { error: profileError } = await supabase
        .from('usuarios')
        .update({
          nome: form.name,
          telefone: form.phone || null,
          avatar_url: form.avatar || null,
        })
        .eq('id', perfil.id);

      if (profileError) {
        toast.error('Login atualizado, mas não foi possível salvar todos os dados do perfil.');
        return;
      }
    }

    toast.success('Perfil atualizado com sucesso.');
  };

  const validatePassword = () => {
    const next = {};
    if (!passwordData.currentPassword) next.currentPassword = 'Senha atual é obrigatória';
    if (!passwordData.newPassword) next.newPassword = 'Nova senha é obrigatória';
    else if (passwordData.newPassword.length < 6) next.newPassword = 'A senha precisa ter no mínimo 6 caracteres';
    if (!passwordData.confirmPassword) next.confirmPassword = 'Confirme a nova senha';
    else if (passwordData.confirmPassword !== passwordData.newPassword) next.confirmPassword = 'As senhas não coincidem';
    if (passwordData.currentPassword && passwordData.currentPassword === passwordData.newPassword) {
      next.newPassword = 'A nova senha precisa ser diferente da senha atual';
    }
    setPasswordErrors(next);
    return Object.keys(next).length === 0;
  };

  const onChangePassword = async () => {
    if (!validatePassword()) return;

    setChangingPassword(true);
    try {
      const email = user?.email || form.email;
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: passwordData.currentPassword,
      });

      if (signInError) {
        setPasswordErrors({ currentPassword: 'Senha atual inválida' });
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: passwordData.newPassword });
      if (error) {
        setPasswordErrors({ newPassword: error.message });
        return;
      }

      setPasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
      toast.success('Senha alterada com sucesso.');
    } finally {
      setChangingPassword(false);
    }
  };

  const fieldClass = (name) => errors[name] ? 'border-danger focus:border-danger focus:ring-danger' : '';
  const passFieldClass = (name) => passwordErrors[name] ? 'border-danger focus:border-danger focus:ring-danger' : '';

  return (
    <div className="space-y-4">
      <PageHeader title="Meu perfil" subtitle="Gerencie seus dados de acesso e perfil da oficina" />
      <Card>
        <div className="mb-5 flex items-center gap-4">
          <div className="relative">
            <Avatar name={form.name || initials} src={form.avatar} className="h-20 w-20 text-xl" />
            <label className="absolute -bottom-1 -right-1 cursor-pointer rounded-full bg-accent p-2 text-white">
              <Camera size={14} />
              <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
            </label>
          </div>
          <div>
            <p className="font-semibold">{form.name || 'Usuário'}</p>
            <p className="text-sm text-zinc-400">{form.email}</p>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Input className={fieldClass('name')} placeholder="Ex: Maria Silva" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            {errors.name && <p className="mt-1 text-xs text-danger">{errors.name}</p>}
          </div>
          <div>
            <Input type="email" className={fieldClass('email')} placeholder="usuario@email.com" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            {errors.email && <p className="mt-1 text-xs text-danger">{errors.email}</p>}
          </div>
          <div>
            <Input className={fieldClass('phone')} placeholder="(00) 00000-0000" mask="phone" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: maskPhone(e.target.value) }))} />
            {errors.phone && <p className="mt-1 text-xs text-danger">{errors.phone}</p>}
          </div>
          <div>
            <Input className={fieldClass('role')} placeholder="Cargo/função na oficina" value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))} />
            {errors.role && <p className="mt-1 text-xs text-danger">{errors.role}</p>}
          </div>
          <div className="md:col-span-2">
            <Input className={fieldClass('workshopName')} placeholder="Nome da oficina" value={form.workshopName} onChange={(e) => setForm((p) => ({ ...p, workshopName: e.target.value }))} />
            {errors.workshopName && <p className="mt-1 text-xs text-danger">{errors.workshopName}</p>}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={onSave}>Salvar alterações</Button>
          <Button variant="ghost" onClick={() => setPasswordModal(true)}>
            <KeyRound size={15} />
            Alterar senha
          </Button>
        </div>
      </Card>

      <Modal open={passwordModal} onClose={() => setPasswordModal(false)} title="Alterar senha">
        <div className="grid gap-3">
          <div>
            <Input type="password" className={passFieldClass('currentPassword')} placeholder="Senha atual" value={passwordData.currentPassword} autoComplete="current-password" onChange={(e) => setPasswordData((p) => ({ ...p, currentPassword: e.target.value }))} />
            {passwordErrors.currentPassword && <p className="mt-1 text-xs text-danger">{passwordErrors.currentPassword}</p>}
          </div>
          <div>
            <Input type="password" className={passFieldClass('newPassword')} placeholder="Nova senha" value={passwordData.newPassword} autoComplete="new-password" onChange={(e) => setPasswordData((p) => ({ ...p, newPassword: e.target.value }))} />
            {passwordErrors.newPassword && <p className="mt-1 text-xs text-danger">{passwordErrors.newPassword}</p>}
          </div>
          <div>
            <Input type="password" className={passFieldClass('confirmPassword')} placeholder="Confirmar nova senha" value={passwordData.confirmPassword} autoComplete="new-password" onChange={(e) => setPasswordData((p) => ({ ...p, confirmPassword: e.target.value }))} />
            {passwordErrors.confirmPassword && <p className="mt-1 text-xs text-danger">{passwordErrors.confirmPassword}</p>}
          </div>
          <div className="flex justify-end">
            <Button onClick={onChangePassword} disabled={changingPassword}>
              {changingPassword ? 'Salvando...' : 'Salvar nova senha'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
